/**
 * ContextSidebar.jsx
 *
 * Reusable collapsible sidebar component for property-level tabs.
 * Provides contextual information and quick actions alongside main content.
 *
 * Branding: H Nawab (#CC0202 red, #262626 dark)
 */

import React, { useState, useEffect } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";

/**
 * SidebarSection - Collapsible section within the sidebar
 */
export function SidebarSection({ title, icon: Icon, children, defaultOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-hn-red" />}
          <span className="text-sm font-semibold text-hn-dark">{title}</span>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-gray-400 transition-transform ${
            isOpen ? "rotate-90" : ""
          }`}
        />
      </button>
      {isOpen && (
        <div className="px-3 pb-3">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * ContextSidebar - Main sidebar component
 */
export default function ContextSidebar({
  title = "Details",
  children,
  width = "w-80",
  defaultCollapsed = false,
  position = "right",
  storageKey = "sidebar-collapsed",
}) {
  // Persist collapsed state
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : defaultCollapsed;
    }
    return defaultCollapsed;
  });

  // Mobile overlay state
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(isCollapsed));
  }, [isCollapsed, storageKey]);

  // Close mobile sidebar on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsMobileOpen(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const toggleCollapsed = () => setIsCollapsed(!isCollapsed);
  const toggleMobile = () => setIsMobileOpen(!isMobileOpen);

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={`hidden lg:flex flex-col bg-white border-l border-gray-200 transition-all duration-300 ${
          isCollapsed ? "w-12" : width
        } ${position === "left" ? "order-first border-l-0 border-r" : ""}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-100">
          {!isCollapsed && (
            <h3 className="text-sm font-semibold text-hn-dark font-lemonmilk">
              {title}
            </h3>
          )}
          <button
            onClick={toggleCollapsed}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronLeft className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
          </button>
        </div>

        {/* Content */}
        {!isCollapsed && (
          <div className="flex-1 overflow-y-auto">
            {children}
          </div>
        )}
      </div>

      {/* Mobile Toggle Button */}
      <button
        onClick={toggleMobile}
        className="lg:hidden fixed bottom-20 right-4 z-40 p-3 bg-hn-red text-white rounded-full shadow-lg hover:bg-red-700 transition-colors"
        title="Toggle sidebar"
      >
        <ChevronLeft className={`w-5 h-5 transition-transform ${isMobileOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={toggleMobile}
          />

          {/* Slide-in Panel */}
          <div
            className={`lg:hidden fixed top-0 ${
              position === "left" ? "left-0" : "right-0"
            } h-full w-80 max-w-[85vw] bg-white z-50 shadow-xl transform transition-transform`}
          >
            {/* Mobile Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-hn-dark font-lemonmilk">
                {title}
              </h3>
              <button
                onClick={toggleMobile}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Mobile Content */}
            <div className="flex-1 overflow-y-auto h-[calc(100%-64px)]">
              {children}
            </div>
          </div>
        </>
      )}
    </>
  );
}
